import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SubscriptionDropdown } from './SubscriptionDropdown';
import type { Subscription } from '@/types/subscriptions';
import { formatCurrency, formatDate } from '@/utils/helpers';

interface SubscriptionCardProps {
  subscription: Subscription;
  variant?: 'active' | 'expired';
  onResubscribe?: (id: string) => void;
  onViewUsage?: (id: string) => void;
  onViewBilling?: (id: string) => void;
  onCancel?: (id: string) => void;
  isCancelling?: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  variant = 'active',
  onResubscribe,
  onViewUsage,
  onViewBilling,
  onCancel,
  isCancelling,
}) => {
  const [iconError, setIconError] = React.useState(false);
  const getBillingText = (): string => {
    if (subscription.plan.interval === 'one_time') {
      return '(One-time)';
    }
    return subscription.plan.interval === 'monthly' ? '/mo' : subscription.plan.interval === 'yearly' ? '/yr' : '';
  };

  const getDateText = (): string => {
    if (variant === 'expired' || subscription.status === 'cancelled') {
      return `${subscription.status === 'cancelled' ? 'Cancelled' : 'Expired'} on ${formatDate(subscription.updated_at)}`;
    }

    if (subscription.status === 'active') {
      return `Active since ${formatDate(subscription.created_at)}`;
    }

    return `Created ${formatDate(subscription.created_at)}`;
  };

  const getServiceIcon = (): string => {
    // Extract first letter of service name for icon
    return subscription.service.name.charAt(0).toUpperCase();
  };

  const isExpired = variant === 'expired';

  return (
    <Card
      className={`bg-white shadow-sm border border-gray-200 rounded-lg${isExpired ? ' opacity-70' : ''}`}
    >
      <div className="p-6">
        {/* Service Header */}
        <div className="flex items-center justify-between pb-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {/* Service Icon */}
            {subscription.service.icon_url && !iconError ? (
              <img
                src={subscription.service.icon_url}
                alt={`${subscription.service.name} icon`}
                className="w-12 h-12 rounded-lg object-cover"
                onError={() => setIconError(true)}
              />
            ) : (
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-xl bg-gray-500"
              >
                {getServiceIcon()}
              </div>
            )}

            {/* Service Info */}
            <div>
              <h3 className={`text-lg font-semibold ${isExpired ? 'text-gray-600' : 'text-gray-900'}`}>
                {subscription.service.name}
              </h3>
              <p className="text-sm text-gray-600">{subscription.service.description}</p>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            {/* Plan and Price */}
            <div className="space-y-4 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{subscription.plan.name}</p>
                  <p className="text-sm text-gray-600">
                    {formatCurrency(subscription.plan.amount_cents / 100)}{getBillingText()}
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                  subscription.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>

              {/* Date Information */}
              <div className="text-sm text-gray-600">
                {getDateText()}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex items-center space-x-2 ml-6">
              {variant === 'expired' ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResubscribe?.(subscription.id)}
                  className="text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Resubscribe
                </Button>
              ) : (
                <SubscriptionDropdown
                  subscriptionId={subscription.id}
                  onViewUsage={onViewUsage}
                  onViewBilling={onViewBilling}
                  onCancel={onCancel}
                  isCancelling={isCancelling}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
